import { useState, useEffect, useRef } from 'react';
import { incidentsApiService } from '../services/incidentsApi';
import { elasticsearchService } from '../services/elasticsearchService';
import { X, Loader, Search, AlertCircle, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';
import { PlayerProfile } from './PlayerProfile';
import { usernameCacheService } from '../services/usernameCache';
import { useCallback } from 'react';
import { EmptyState } from './ui/EmptyState';

interface LinkUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  incidentId: string;
}

interface PlayerSearchResult {
  _id: string;
  _source: {
    uuid: string;
    lastKnownName: string;
    nickname: string;
    lastKnownIp: string;
  };
}

export const LinkUserModal = ({ isOpen, onClose, onSuccess, incidentId }: LinkUserModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLinking, setIsLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkedUsers, setLinkedUsers] = useState<string[]>([]);

  const debouncedSearch = useDebounce(searchQuery, 300);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch already linked users
  useEffect(() => {
    if (isOpen) {
      fetchLinkedUsers();
    }
  }, [isOpen, incidentId]);

  // Search for players
  useEffect(() => {
    if (isOpen && debouncedSearch.length >= 2) {
      searchPlayers();
    } else if (debouncedSearch.length < 2) {
      setResults([]);
    }
  }, [debouncedSearch, isOpen]);

  const fetchLinkedUsers = async () => {
    try {
      const users = await incidentsApiService.getUsersForIncident(incidentId);
      setLinkedUsers(users.map(u => u.user_uuid));
    } catch (err) {
      console.error('Failed to fetch linked users:', err);
    }
  };

  const searchPlayers = async () => {
    setLoading(true);
    setError(null);

    try {
      const searchResults = await elasticsearchService.searchPlayers(debouncedSearch);
      setResults(searchResults.hits.hits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search players');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (userUuid: string) => {
    setIsLinking(userUuid);
    setError(null);

    try {
      await incidentsApiService.linkUser(incidentId, userUuid);
      setLinkedUsers(prev => [...prev, userUuid]);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link user');
    } finally {
      setIsLinking(null);
    }
  };

  const handleUsernamesUpdated = useCallback(() => {
    results.forEach(result => {
      if (!result._source.lastKnownName) {
        const newName = usernameCacheService.getName(result._source.uuid);
        if (newName) {
          elasticsearchService.updatePlayerName(result._id, newName)
            .catch(err => console.error(`Failed to update name for ${result._source.uuid}:`, err));
        }
      }
    });
  }, [results]);

  useEffect(() => {
    if (results.length > 0) {
      const uuids = results.map((hit: any) => hit._source.uuid);
      usernameCacheService.prefetchUsernames(uuids);
    }

    document.addEventListener('usernamesUpdated', handleUsernamesUpdated);
    return () => {
      document.removeEventListener('usernamesUpdated', handleUsernamesUpdated);
    };
  }, [results, handleUsernamesUpdated]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const isPreciseIpSearch = (query: string) => {
    const ipRegex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d+/;
    return ipRegex.test(query);
  };

  const filteredResults = results.filter(result => !linkedUsers.includes(result._source.uuid));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-2xl max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-user-title"
        >
          <div className="modal-header">
            <div className="flex items-center gap-3 min-w-0">
              <span className="icon-tile icon-tile-accent">
                <Users className="w-4 h-4" />
              </span>
              <h2 id="link-user-title" className="modal-title">Link Minecraft User</h2>
            </div>
            <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input w-full pl-9 pr-9"
              placeholder="Search by name, UUID, or IP..."
              aria-label="Search players"
            />
            {loading && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 animate-spin" />}
          </div>

          <div className="modal-body">
            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <EmptyState
                bare
                compact
                icon={Search}
                title="Enter at least 2 characters to search"
                description="You can search by username, UUID or IP address."
              />
            )}

            {filteredResults.length === 0 && searchQuery.length >= 2 && !loading && (
              <EmptyState
                bare
                compact
                icon={Users}
                title="No players found"
                description="Players already linked to this incident are hidden."
              />
            )}

            {filteredResults.length > 0 && (
              <div className="space-y-2">
                {filteredResults.map((result) => (
                  <div
                    key={result._id}
                    className="subcard p-3 flex items-center justify-between gap-3 hover:border-glass-200/25 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <PlayerProfile
                        uuid={result._source.uuid}
                        name={result._source.lastKnownName}
                        nickname={result._source.nickname}
                        ipAddress={isPreciseIpSearch(debouncedSearch) && result._source.lastKnownIp.startsWith(debouncedSearch) ? result._source.lastKnownIp : undefined}
                        showUuid={true}
                      />
                    </div>
                    <button
                      onClick={() => handleLink(result._source.uuid)}
                      disabled={isLinking === result._source.uuid}
                      className="glass-button-sm shrink-0 whitespace-nowrap"
                    >
                      {isLinking === result._source.uuid ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Linking...
                        </>
                      ) : (
                        'Link User'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
